import { ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { type CallRecord } from '@frontend/api-call-intel';
import { useTranslation, Trans } from '@frontend/i18n';
import { CheckboxField, Modal, ModalControlModalProps, Text, useFormField } from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../../hooks/use-call-intel-defaults';
import { KeyMomentRemovalChange } from '../../utils/call-analysis-util';
import { generateTrackingId } from '../../utils/tracking';
import { CallIntelChip } from '../chip/call-intel-chip';
import { SchedulingChip } from '../chip/scheduling-chip';

type KeyMomentChangesProps = {
  call: CallRecord;
  changes: KeyMomentRemovalChange[];
};

type RemoveKeyMomentsModalProps = KeyMomentChangesProps & {
  modalProps: ModalControlModalProps;
  isDemoAccount?: boolean;
  onCancel: () => void;
  onConfirm: (dontShowAgain: boolean) => void;
};

type ChangeTransitionProps = {
  call: CallRecord;
  change: KeyMomentRemovalChange;
};

type TransitionProps = {
  from: ReactNode;
  to: ReactNode;
};

const Transition = ({ from, to }: TransitionProps) => {
  const { t } = useTranslation('call-intel-core');

  return (
    <div css={styles.transition}>
      {from}
      <Text as='span'>{t('to')}</Text>
      {to}
    </div>
  );
};

const ChangeTransition = ({ call, change }: ChangeTransitionProps) => {
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  if (change.field === 'callSentiment') {
    return (
      <Transition
        from={
          <CallIntelChip
            as='chip'
            chipVariants={chipVariants}
            dataKey={change.from}
            label={dataLabels.sentiments?.[change.from] ?? change.from}
            hideTooltip
          />
        }
        to={
          <CallIntelChip
            as='chip'
            chipVariants={chipVariants}
            dataKey={change.to}
            label={dataLabels.sentiments?.[change.to] ?? change.to}
            hideTooltip
          />
        }
      />
    );
  }

  if (change.field === 'schedulingOutcomeV2') {
    return (
      <Transition
        from={<SchedulingChip call={call} schedulingOutcome={change.from} showTooltip={false} />}
        to={<SchedulingChip call={call} schedulingOutcome={change.to} showTooltip={false} />}
      />
    );
  }

  return null;
};

const ChangeList = ({ call, changes }: KeyMomentChangesProps) => {
  const { t } = useTranslation('call-intel-core');

  return (
    <div css={styles.changes}>
      {changes.map((change, index) => {
        const hasNextChange = index < changes.length - 1;

        return (
          <div key={change.id} css={styles.change}>
            <ChangeTransition call={call} change={change} />

            {hasNextChange && <Text as='span'>{t('and')}</Text>}
          </div>
        );
      })}
    </div>
  );
};

const formatKeyMomentLabels = (labels: string[], conjunction: string): string => {
  if (!labels.length) {
    return '';
  }

  if (labels.length === 1) {
    return labels[0];
  }

  if (labels.length === 2) {
    return `${labels[0]} ${conjunction} ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(', ')} ${conjunction} ${labels[labels.length - 1]}`;
};

export const RemoveKeyMomentsModal = ({
  call,
  changes,
  modalProps,
  isDemoAccount,
  onCancel,
  onConfirm,
}: RemoveKeyMomentsModalProps) => {
  const { t } = useTranslation('call-intel-core');

  const fieldProps = useFormField({
    type: 'checkbox',
    value: false,
  });

  const keyMomentLabels = useMemo(
    () =>
      formatKeyMomentLabels(
        changes.map(({ keyMomentLabel }) => keyMomentLabel),
        t('and')
      ),
    [changes, t]
  );

  const isSingleKeyMoment = changes.length === 1;

  const title = isSingleKeyMoment
    ? t('Remove {{keyMoment}} Key Moment?', {
        keyMoment: keyMomentLabels,
      })
    : t('Remove Key Moments?');

  const removedKeyMomentDescription = isSingleKeyMoment
    ? t('the {{keyMoment}} Key Moment', {
        keyMoment: keyMomentLabels,
      })
    : t('the {{keyMoments}} Key Moments', {
        keyMoments: keyMomentLabels,
      });

  const handleConfirm = () => {
    onConfirm(!!fieldProps.value);
  };

  return (
    <Modal {...modalProps} maxWidth={400} onClose={onCancel}>
      <Modal.Header onClose={onCancel}>{title}</Modal.Header>

      <Modal.Body>
        <div css={styles.body}>
          <div>
            <Trans t={t} values={{ keyMoments: removedKeyMomentDescription }}>
              <Text>Changing the analysis from</Text>
              <ChangeList call={call} changes={changes} />
              <Text>will remove {{ keyMoments: removedKeyMomentDescription }} of the Conversation.</Text>
            </Trans>
          </div>

          <Text>
            {t(
              'The original AI analysis, including {{keyMoments}}, can be viewed in the Conversation Analysis History after changes are saved.',
              { keyMoments: removedKeyMomentDescription }
            )}
          </Text>

          <CheckboxField
            {...fieldProps}
            name='dontShowKeyMomentRemovalAgain'
            label={t("Don't show this again")}
            trackingId={generateTrackingId({
              component: 'remove-key-moments-modal',
              context: 'dont-show-again-checkbox-click',
              isDemoAccount,
            })}
          />
        </div>
      </Modal.Body>

      <Modal.Footer
        secondary={{
          label: t('No, Cancel'),
          onClick: onCancel,
        }}
        primary={{
          label: t('Yes, Save Changes'),
          onClick: handleConfirm,
        }}
      />
    </Modal>
  );
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-lg);
  `,
  changes: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
  `,

  change: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
  `,

  transition: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
  `,
};
